import React, { useState } from 'react';
import { Plus, Trash2, AlertCircle, Map, ChevronDown, ChevronRight, UserPlus, X } from 'lucide-react';
import { 
  useGetZonesQuery, 
  useCreateZoneMutation, 
  useDeleteZoneMutation, 
  useAssignSensorMutation, 
  useRemoveSensorMutation,
  useUpdateZoneUsersMutation
} from '../services/zoneApi';
import { useGetSensorListQuery } from '../services/sensorApi';
import { useGetUsersQuery } from '../services/userApi';
import ZoneSensors from '../components/zones/ZoneSensors';
import { usePermissions } from '../hooks/usePermissions';
import ZoneUsers from '../components/zones/ZoneUsers';
import { useDispatch } from 'react-redux';
import { zoneApi } from '../services/zoneApi';

interface ZoneFormData {
  name: string;
}

const initialFormState: ZoneFormData = {
  name: '',
};

export default function ZonesPage() {
  const { canManageZones } = usePermissions();
  const dispatch = useDispatch();
  const [isAddingZone, setIsAddingZone] = useState(false);
  const [expandedZones, setExpandedZones] = useState<Set<number>>(new Set());
  const [formData, setFormData] = useState<ZoneFormData>(initialFormState);
  const [formError, setFormError] = useState<string | null>(null);
  const [selectedZoneForUsers, setSelectedZoneForUsers] = useState<number | null>(null);

  const { data: zones, isLoading: zonesLoading, error: zonesError, refetch: refetchZones } = useGetZonesQuery(undefined, { refetchOnMountOrArgChange: true });
  const { data: sensors } = useGetSensorListQuery();
  const [createZone] = useCreateZoneMutation();
  const [deleteZone] = useDeleteZoneMutation();
  const [assignSensor] = useAssignSensorMutation();
  const [removeSensor] = useRemoveSensorMutation();
  const [updateZoneUsers] = useUpdateZoneUsersMutation();
  const { data: users, isLoading: usersLoading } = useGetUsersQuery(undefined, {
    skip: !selectedZoneForUsers
  });

  const toggleZone = (zoneId: number) => {
    const newExpanded = new Set(expandedZones);
    if (expandedZones.has(zoneId)) {
      newExpanded.delete(zoneId);
    } else {
      newExpanded.add(zoneId);
    }
    setExpandedZones(newExpanded);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canManageZones) return;
    setFormError(null);

    try {
      await createZone({ zoneName: formData.name }).unwrap();
      setFormData(initialFormState);
      setIsAddingZone(false);
    } catch (error) {
      setFormError('Failed to create zone. Please try again.');
    }
  };

  const handleDelete = async (zoneId: number) => {
    if (!canManageZones) return;
    if (window.confirm('Are you sure you want to delete this zone?')) {
      try {
        setFormError(null);
        await deleteZone(zoneId).unwrap();
        setExpandedZones(prev => {
          const newSet = new Set(prev);
          newSet.delete(zoneId);
          return newSet;
        });
      } catch (error: any) {
        console.error('Failed to delete zone:', error);
        setFormError(error?.data?.message || 'Failed to delete zone. Please try again.');
      }
    }
  };

  const handleAssignSensor = async (zoneId: number, sensorId: number) => {
    if (!canManageZones) return;
    try {
      setFormError(null);
      if (!zoneId || !sensorId) {
        throw new Error('Zone ID and sensor ID are required');
      }
      await assignSensor({ zoneId, sensorId }).unwrap();
      dispatch(zoneApi.util.invalidateTags(['Zones']));
      dispatch(zoneApi.util.resetApiState());
      await refetchZones();
    } catch (error) {
      console.error('Failed to assign sensor:', error);
      setFormError(error instanceof Error ? error.message : 'Failed to assign sensor to zone');
    }
  };

  const handleRemoveSensor = async (zoneId: number, sensorId: number) => {
    if (!canManageZones) return;
    try {
      setFormError(null);
      if (!zoneId || !sensorId) {
        throw new Error('Zone ID and sensor ID are required');
      }
      await removeSensor({ zoneId, sensorId }).unwrap();
      dispatch(zoneApi.util.invalidateTags(['Zones']));
      dispatch(zoneApi.util.resetApiState());
      await refetchZones();
    } catch (error) {
      console.error('Failed to remove sensor:', error);
      setFormError(error instanceof Error ? error.message : 'Failed to remove sensor from zone');
    }
  };

  const handleAssignUser = async (userIds: number[]) => {
    if (!selectedZoneForUsers) return;
    try {
      // Get current zone and its users
      const currentZone = zones?.find(z => z.zone_id === selectedZoneForUsers);
      const currentUserIds = getAssignedUserIds(currentZone);
      
      // Merge existing users with new users, removing duplicates
      const mergedUserIds = [...new Set([...currentUserIds, ...userIds])];
      
      console.log('Updating users with:', mergedUserIds);
      
      await updateZoneUsers({ 
        zoneId: selectedZoneForUsers, 
        userIds: mergedUserIds 
      }).unwrap();
      
      // Refetch zones to get updated user assignments
      await refetchZones();
    } catch (error) {
      console.error('Failed to assign user:', error);
      setFormError('Failed to assign user to zone');
    }
  };

  const handleRemoveUser = async (userId: number) => {
    if (!selectedZoneForUsers) return;
    try {
      const currentZone = zones?.find(z => z.zone_id === selectedZoneForUsers);
      const currentUserIds = getAssignedUserIds(currentZone);
      const newUserIds = currentUserIds.filter(id => id !== userId);
      
      console.log('Removing user, new user list:', newUserIds);
      
      await updateZoneUsers({ 
        zoneId: selectedZoneForUsers, 
        userIds: newUserIds 
      }).unwrap();
      
      // Refetch zones to get updated user assignments
      await refetchZones();
    } catch (error) {
      console.error('Failed to remove user:', error);
      setFormError('Failed to remove user from zone');
    }
  };

  // Helper function to get assigned user IDs
  const getAssignedUserIds = (zone: any) => {
    if (!zone?.zone_users) return [];
    if (Array.isArray(zone.zone_users[0])) return zone.zone_users[0];
    if (Array.isArray(zone.zone_users)) return zone.zone_users;
    return [];
  };

  // Helper function to get user count
  const getUserCount = (zone: any) => {
    const userIds = getAssignedUserIds(zone);
    return userIds.length;
  };

  if (zonesLoading) {
    return <div className="p-6">Loading zones...</div>;
  }

  if (zonesError) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-md flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
          <span className="text-red-700 dark:text-red-400">Failed to load zones</span>
        </div>
      </div>
    );
  }

  // Debug log to verify zones data updates
  console.log('Zones data:', zones);

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Zones</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {canManageZones ? 'Manage your zones' : 'View zones'}
          </p>
        </div>
        
        {canManageZones && (
          <button
            onClick={() => setIsAddingZone(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md flex items-center"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Zone
          </button>
        )}
      </div>

      {formError && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 p-4 rounded-md">
          <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
        </div>
      )}

      {isAddingZone && (
        <div className="mb-6 bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Add New Zone</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Zone Name
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setIsAddingZone(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
              >
                Create Zone
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Zone Name
              </th>
              <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Sensors
              </th>
              <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Users
              </th>
              <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {zones?.map((zone) => (
              <React.Fragment key={zone.zone_id}>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <button
                        onClick={() => toggleZone(zone.zone_id)}
                        className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full mr-2 flex-shrink-0"
                      >
                        {expandedZones.has(zone.zone_id) ? (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                      <Map className="h-5 w-5 text-gray-400 mr-2 hidden sm:block" />
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                          {zone.zone_name}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                      {zone.sensor_count} sensors
                    </span>
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                        {getUserCount(zone)} users
                      </span>
                      {canManageZones && (
                        <button
                          onClick={() => setSelectedZoneForUsers(zone.zone_id)}
                          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full"
                          title="Manage Users"
                        >
                          <UserPlus className="h-5 w-5 text-gray-400" />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-right">
                    {canManageZones && (
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleDelete(zone.zone_id)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                {expandedZones.has(zone.zone_id) && (
                  <tr>
                    <td colSpan={4} className="bg-gray-50 dark:bg-gray-800/50">
                      {canManageZones && (
                        <ZoneSensors
                          zoneId={zone.zone_id}
                          sensors={sensors ?? []}
                          onAssign={handleAssignSensor}
                          onRemove={handleRemoveSensor}
                          zonesLoading={zonesLoading}
                        />
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* User Management Modal */}
      {selectedZoneForUsers && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Manage Users for {zones?.find(z => z.zone_id === selectedZoneForUsers)?.zone_name}
              </h3>
              <button
                onClick={() => setSelectedZoneForUsers(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {usersLoading ? (
              <div className="text-center py-4">Loading users...</div>
            ) : users ? (
              <div className="p-4 space-y-4">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Add Users
                  </label>
                  <select
                    multiple
                    size={4}
                    className="select select-bordered w-full"
                    onChange={(e) => {
                      const selectedOptions = Array.from(e.target.selectedOptions, option => Number(option.value));
                      console.log('Selected users:', selectedOptions);
                      handleAssignUser(selectedOptions);
                    }}
                  >
                    {users
                      ?.filter(user => {
                        const currentZone = zones?.find(z => z.zone_id === selectedZoneForUsers);
                        const assignedUserIds = getAssignedUserIds(currentZone);
                        return !assignedUserIds.includes(user.id);
                      })
                      .map(user => (
                        <option key={user.id} value={user.id}>
                          {user.firstName} {user.lastName} ({user.email})
                        </option>
                      ))}
                  </select>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Hold Ctrl/Cmd to select multiple users
                  </p>
                </div>

                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Assigned Users
                  </h4>
                  <div className="space-y-2">
                    {users
                      ?.filter(user => {
                        const currentZone = zones?.find(z => z.zone_id === selectedZoneForUsers);
                        const assignedUserIds = getAssignedUserIds(currentZone);
                        return assignedUserIds.includes(user.id);
                      })
                      .map(user => (
                        <div key={user.id} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
                          <div>
                            <span className="font-medium">{user.firstName} {user.lastName}</span>
                            <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                              {user.email}
                            </span>
                          </div>
                          <button
                            onClick={() => handleRemoveUser(user.id)}
                            className="text-red-600 hover:text-red-700 dark:text-red-400"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500">No users found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}